import { useHistory } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useQueryparam } from './useQueryparam';
import * as ROUTES from '../_constants/routes';
import { okta } from '../_services/okta.service';
import { toggleSideNavigation } from '../_actions/sideNav.actions';
import { setLoginError, setLoginInitial, setLoginLoading } from '../_actions/login.actions';
import { loginSelector } from '../_selectors';
import { LoginResponse, LoginState } from '../_types/login';
import { useIsTextBuy } from './useIsTextBuy';
import { useSalesChannel } from './useSalesChannel';

export interface OktaProfile {
    isRegistered: boolean;
    isComplete: boolean;
    isVerified: boolean;
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    token: Record<string, any>;
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    profile: Record<string, any>;
}

export type OktaProfileFillableData = {
    phoneNumber: string;
    givenName: string;
    familyName: string;
    optInStatus: boolean;
} | null;

export function useOktaAuth(): {
    oktaProfile: OktaProfile | null;
    data: LoginState['data'];
    error: LoginState['error'];
    loading: LoginState['loading'];
    signIn: (data: OktaProfileFillableData) => void;
    ballparkSignIn: (data: { mlbamOkta?: string | null; mlbEmail?: string | null }) => void;
} {
    const history = useHistory();
    const dispatch = useDispatch();

    const { data, error, loading } = useSelector(loginSelector);

    const { textBuyUI, salesChannelId } = useSalesChannel();
    const isTextBuy = useIsTextBuy();

    /**
     * authCode returned by Okta, to be exchanged with an access token.
     */
    const authCode = useMemo(() => useQueryparam().get('code'), []);
    const errorMessage = useMemo(() => useQueryparam().get('error_description'), []);

    /**
     * Okta Profile payload as returned by
     * upgrade-ui (/api/:salesChannelId/oktaAuth).
     *
     * We'll have this when authCode is retrieved, and successfully exchanged.
     */
    const [oktaProfile, setOktaProfile] = useState<null | OktaProfile>(null);

    /**
     * Exchange Okta Auth code with access token and profile details.
     */
    const authenticateOktaProfile = async (code: string) => {
        if (code.length < 30) {
            console.warn('Invalid authCode');
            dispatch(setLoginError({ message: 'Okta login failed!' }));
            return;
        }

        dispatch(setLoginLoading());

        try {
            const profile = await dispatch<OktaProfile>(okta.authenticate(salesChannelId, code) as any);

            setOktaProfile(profile);
        } catch (e) {
            dispatch(setLoginInitial());
        }
    };

    /**
     * If Okta Auth code is found, then initiate login.
     */
    useEffect(() => {
        if (!textBuyUI || !authCode || typeof authCode !== 'string') {
            return;
        }

        authenticateOktaProfile(authCode);
    }, [authCode]);

    /**
     * If Okta error detail is found, show error message.
     */
    useEffect(() => {
        if (errorMessage) {
            console.error('ERROR: ' + errorMessage);
            dispatch(setLoginError({ message: 'Login failed.' }));
        }
    }, [errorMessage]);

    /**
     * Sign in..
     * Call this once you're ready to sign the user in / register
     */
    const signIn = async (data: OktaProfileFillableData) => {
        if (!oktaProfile) {
            return;
        }

        dispatch(setLoginLoading());
        dispatch(okta.signIn(salesChannelId, oktaProfile, data));
    };

    const ballparkSignIn = async (data: { mlbamOkta?: string | null; mlbEmail?: string | null }) => {
        if (!data.mlbamOkta && !data.mlbEmail) {
            return;
        }

        dispatch(setLoginLoading());
        dispatch(okta.ballparkSignIn(salesChannelId, data));
    };

    /**
     * Process logged in user.
     * Redirect them to the correct page.
     */
    const handleLoggedInUser = (loginData: LoginResponse) => {
        sessionStorage.setItem('signupToken', loginData.signupToken);
        sessionStorage.removeItem('source');

        /**
         * If the user has already set up their account,
         * redirect them to the settings page...
         */
        if (loginData.completedAt) {
            sessionStorage.setItem('loggedIn', 'true');
            dispatch(toggleSideNavigation(true));
            history.replace(!isTextBuy ? ROUTES.UPDATE_PAYMENT_CARD : ROUTES.SHARE_INVITE);
            return;
        }

        if (!loginData.completedAt) {
            return;
        }
        // Otherwise, redirect to finalize signup page.
        const route = isTextBuy ? ROUTES.TTB_FINALIZE_SIGNUP : ROUTES.FINALIZE_SIGNUP;
        // TODO find better way to pass param to route
        history.replace(route.replace(':signupToken', loginData.signupToken), { values: data });
    };

    /**
     * When the user is logged in)...
     */
    useEffect(() => {
        if (data) {
            handleLoggedInUser(data);
        }
    }, [data]);

    /**
     * Handle login error
     */
    useEffect(() => {
        if (error) {
            sessionStorage.removeItem('loggedIn');
        }
    }, [error]);

    return {
        // State
        oktaProfile,

        // Login state
        data,
        error,
        loading,

        // Actions
        signIn,
        ballparkSignIn,
    };
}
